import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, CircleDollarSign, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useVerificationWarning } from "@/hooks/useVerificationWarning";
import { VerificationWarningModal } from "@/components/verification/VerificationWarningModal";

interface NegotiateRateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  load: {
    loadId?: string;
    originCity: string;
    originState: string;
    destCity: string;
    destState: string;
    rate: number;
    miles: number;
    equipment: string;
    pickupDate: string;
  };
}

export default function NegotiateRateModal({ open, onOpenChange, load }: NegotiateRateModalProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { showWarningOrRun, modalProps } = useVerificationWarning();
  const [rateAmount, setRateAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const proposedRate = parseFloat(rateAmount);
  const isValid = !isNaN(proposedRate) && proposedRate > 0 && !!load.loadId;
  const rpm = isValid && load.miles > 0 ? (proposedRate / load.miles).toFixed(2) : "—";

  const handleSubmit = () => {
    if (!isValid || !user) return;
    showWarningOrRun(() => { void doSubmit(); });
  };

  const doSubmit = async () => {
    if (!isValid || !user) return;
    setSubmitting(true);

    try {
      // Get carrier's company_id
      const { data: cuData } = await supabase
        .from("company_users")
        .select("company_id")
        .eq("user_id", user.id)
        .eq("is_active", true)
        .limit(1)
        .maybeSingle();

      if (!cuData?.company_id) {
        toast({ variant: "destructive", title: "No company found", description: "You must belong to an active company to submit offers." });
        setSubmitting(false);
        return;
      }

      const { data, error } = await supabase.rpc("carrier_submit_offer", {
        p_load_id: load.loadId!,
        p_carrier_company_id: cuData.company_id,
        p_rate_amount: proposedRate,
        p_notes: notes.trim() || null,
        p_expires_minutes: 30,
      });

      if (error) throw error;
      const result = data as { success: boolean; error?: string };
      if (!result?.success) {
        toast({ variant: "destructive", title: "Cannot submit offer", description: result?.error || "Failed to submit offer" });
        setSubmitting(false);
        return;
      }

      toast({ title: "Offer submitted!", description: `Your rate of $${proposedRate.toLocaleString()} has been sent to the broker.` });
      await queryClient.invalidateQueries({ queryKey: ["carrier-offers"] });
      await queryClient.invalidateQueries({ queryKey: ["carrier-offers-full"] });
      setRateAmount("");
      setNotes("");
      onOpenChange(false);
    } catch (err: any) {
      console.error("Negotiate rate error:", err);
      toast({ variant: "destructive", title: "Error", description: err.message || "Failed to submit offer" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CircleDollarSign className="h-5 w-5 text-brand-teal" />
            Negotiate Rate
          </DialogTitle>
          <DialogDescription>
            Submit your proposed rate for this load. The broker will be notified.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Load summary */}
          <div className="bg-muted/50 rounded-lg p-3 space-y-1">
            <div className="flex items-center gap-1.5 text-sm font-semibold">
              <span>{load.originCity}, {load.originState}</span>
              <ArrowRight className="h-3.5 w-3.5 text-brand-teal" />
              <span>{load.destCity}, {load.destState}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>{load.miles.toLocaleString()} mi</span>
              <span>·</span>
              <span>{load.equipment}</span>
              <span>·</span>
              <span>{load.pickupDate}</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="secondary" className="text-xs">Posted: ${load.rate.toLocaleString()}</Badge>
              <span className="text-xs text-muted-foreground">${(load.miles > 0 ? load.rate / load.miles : 0).toFixed(2)}/mi</span>
            </div>
          </div>

          {/* Rate input */}
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">Your Proposed Rate ($)</label>
            <Input
              type="number"
              placeholder="e.g. 2500"
              value={rateAmount}
              onChange={(e) => setRateAmount(e.target.value)}
              min={1}
              step={50}
            />
            {isValid && (
              <p className="text-xs text-muted-foreground mt-1">${rpm}/mi · {proposedRate > load.rate ? `+$${(proposedRate - load.rate).toLocaleString()} above` : proposedRate < load.rate ? `-$${(load.rate - proposedRate).toLocaleString()} below` : "At"} posted rate</p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">Notes <span className="text-muted-foreground font-normal">(optional)</span></label>
            <Textarea
              placeholder="Can pick up same day, team drivers available..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={500}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!isValid || submitting} className="bg-brand-teal text-white hover:bg-brand-teal/90">
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Submit Offer
          </Button>
        </DialogFooter>
      </DialogContent>
      <VerificationWarningModal {...modalProps} />
    </Dialog>
  );
}
